'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';

// styles
import styles from './media-overlay.module.scss';

// types
import type { MediaItem, OverlayNordics } from './types';
import type { AspectRatioType } from 'components/image/aspect-ratio';

// components
import { Image } from 'components/image/image';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { CmsLink } from 'components/Link/CmsLink';

// utils
import { calculateImageDimensions, truncateHtmlTextNodes } from './media-overlay.utils';

type ImageObject = {
  url: string;
  format?: string;
  aspectRatio?: AspectRatioType;
};

type ImageObjectMobile = {
  url: string;
  format?: string;
};

type OverlayContentProps = {
  overlay: OverlayNordics;
};

type ImageContentProps = {
  mediaItem: MediaItem;
  getImage: (item: MediaItem) => ImageObject | ImageObjectMobile | string;
};

function OverlayContent({ overlay }: Readonly<OverlayContentProps>) {
  const hasContent = overlay?.text || overlay?.link;

  const truncatedText = useMemo(
    () => (overlay.text ? truncateHtmlTextNodes(overlay.text) : ''),
    [overlay.text],
  );

  if (!hasContent) return null;

  return (
    <div className={styles.overlayContent}>
      {overlay.text && (
        <InnerHtml
          previewId="#st_overlayText"
          as="div"
          content={truncatedText}
          stylingType="inverted"
          isGdds
        />
      )}
      {overlay.link && <CmsLink stylingType="inverted" link={overlay.link} />}
    </div>
  );
}

export function ImageContent({ mediaItem, getImage }: ImageContentProps) {
  const [maxHeight, setMaxHeight] = useState<number>(0);

  const image = getImage(mediaItem);
  const aspectRatio: AspectRatioType | undefined = mediaItem.pictureObject?.aspectRatio;
  const overlay = mediaItem.overlay as OverlayNordics;

  const imageData = {
    data: image as any,
    alt: mediaItem.pictureAlt,
    title: mediaItem.pictureAlt,
    src: typeof image === 'string' ? image : (image as any)?.url,
    format: typeof image === 'string' ? undefined : (image as any)?.format,
  };

  const customStyles = {
    '--max-image-height': `${maxHeight}px`,
  } as React.CSSProperties;

  const updateImageDimensions = useCallback(() => {
    const { maxHeight: newMaxHeight } = calculateImageDimensions();
    setMaxHeight(newMaxHeight);
  }, []);

  useEffect(() => {
    updateImageDimensions();
    window.addEventListener('resize', updateImageDimensions);

    return () => {
      window.removeEventListener('resize', updateImageDimensions);
    };
  }, [updateImageDimensions]);

  return (
    <div style={customStyles}>
      <Image
        data={imageData.data}
        alt={imageData.alt}
        title={imageData.title}
        src={imageData.src}
        format={imageData.format}
        aspectRatio={aspectRatio}
        shouldHaveImgWrapper
      />
      <OverlayContent overlay={overlay} />
    </div>
  );
}
